'use client'

import {
  Checkbox,
  Divider,
  List,
  ListItemText,
  ListSubheader,
  MenuItem,
  Typography,
} from '@mui/material'
import { Fragment, useCallback, useId, type KeyboardEvent } from 'react'
import { FilterDropdown } from '../FilterDropdown'
import {
  getOptionState,
  getSummaryLabels,
  isOptionDisabled,
  toggleOption,
  type FilterMultiSelectOption,
  type FilterMultiSelectSection,
} from './helpers'

export type { FilterMultiSelectOption, FilterMultiSelectSection }

// How many summary labels the chip spells out before falling back to
// "N selected".
const MAX_SUMMARY_LABELS = 2

const ROW = '[role="menuitemcheckbox"]'
const ENABLED_ROW = `${ROW}:not([aria-disabled="true"])`
const CHECKED_ROW = `${ROW}:not([aria-checked="false"])`

const isEnabled = (row: HTMLElement) =>
  row.getAttribute('aria-disabled') !== 'true'

// A multi-select filter dropdown with checkbox options, optional titled
// sections, and group options that select several values at once (rendering
// an indeterminate checkbox while only some of their values are selected).
// The panel stays open across clicks — multi-selection is exploratory.
export function FilterMultiSelect({
  label,
  summaryPrefix,
  sections,
  value,
  onChange,
}: {
  label: string
  // Chip text when active is `${summaryPrefix}: ${summary}`.
  summaryPrefix: string
  sections: FilterMultiSelectSection[]
  value: string[]
  onChange: (value: string[]) => void
}) {
  const headerIdBase = useId()
  const labels = getSummaryLabels(sections, value)
  const summary =
    value.length === 0
      ? null
      : `${summaryPrefix}: ${
          labels.length > MAX_SUMMARY_LABELS
            ? `${String(labels.length)} selected`
            : labels.join(', ')
        }`

  // A ref callback rather than autoFocus (which the a11y lint rejects); stable
  // so it doesn't re-take focus on every toggle.
  const focusFirstCheckedRow = useCallback((menu: HTMLDivElement | null) => {
    const row =
      menu?.querySelector<HTMLElement>(CHECKED_ROW) ??
      menu?.querySelector<HTMLElement>(ENABLED_ROW)
    row?.focus()
  }, [])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Located among all rows, not just enabled ones: unchecking a zero-count
    // row disables it while it still has focus, and arrows must still move to
    // its neighbour rather than jump to an end.
    const rows = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(ROW)
    )
    const enabledRows = rows.filter(isEnabled)
    if (enabledRows.length === 0) return
    // The checkbox input can hold focus after a mouse click, so resolve the
    // key's target up to its row. -1 when the menu itself has focus.
    const index = rows.findIndex(
      (row) => row === (event.target as HTMLElement).closest(ROW)
    )
    const first = enabledRows[0]
    const last = enabledRows[enabledRows.length - 1]
    let next: HTMLElement
    switch (event.key) {
      case 'ArrowDown':
        next = rows.slice(index + 1).find(isEnabled) ?? first
        break
      case 'ArrowUp':
        next =
          (index > 0 ? rows.slice(0, index).reverse().find(isEnabled) : null) ??
          last
        break
      case 'Home':
        next = first
        break
      case 'End':
        next = last
        break
      default:
        return
    }
    event.preventDefault()
    next.focus()
  }

  return (
    <FilterDropdown
      label={label}
      summary={summary}
      onClear={() => {
        onChange([])
      }}
    >
      {/* Grow with the viewport (long lists like the 16-row grades panel can
          show fully on tall screens) but never below the old 360px cap, so
          short screens are no worse off. The 240px headroom covers the page
          header plus the trigger row above the popover. */}
      {/* Not MUI's MenuList: its arrow keys walk only its direct children, and
          a titled section's rows sit inside that section's group. Each section
          is its own list, as in the W3C grouped-options example. */}
      <List
        component="div"
        ref={focusFirstCheckedRow}
        role="menu"
        // Rows are tabindex -1, so the menu itself is the tab stop that lets
        // Tab back in; ArrowDown from it lands on the first row.
        tabIndex={0}
        onKeyDown={handleKeyDown}
        disablePadding
        sx={{
          maxHeight: 'max(360px, calc(100vh - 240px))',
          overflowY: 'auto',
        }}
      >
        {sections.map((section, index) => {
          const sectionKey = section.title ?? String(index)
          const headerId = `${headerIdBase}-${String(index)}`
          return (
            <Fragment key={sectionKey}>
              {index > 0 ? <Divider sx={{ my: 1 }} /> : null}
              <List
                disablePadding
                {...(section.title
                  ? { role: 'group', 'aria-labelledby': headerId }
                  : { role: 'presentation' })}
              >
                {section.title ? (
                  <ListSubheader
                    id={headerId}
                    role="presentation"
                    disableSticky
                    sx={{
                      py: 0.5,
                      lineHeight: 1.5,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'text.disabled',
                    }}
                  >
                    {section.title}
                  </ListSubheader>
                ) : null}
                {section.options.map((option) => (
                  <OptionRow
                    key={option.label}
                    option={option}
                    state={getOptionState(option, value)}
                    disabled={isOptionDisabled(option, value)}
                    onToggle={() => {
                      onChange(toggleOption(option, value))
                    }}
                  />
                ))}
              </List>
            </Fragment>
          )
        })}
      </List>
    </FilterDropdown>
  )
}

function OptionRow({
  option,
  state,
  disabled,
  onToggle,
}: {
  option: FilterMultiSelectOption
  state: { checked: boolean; indeterminate: boolean }
  disabled: boolean
  onToggle: () => void
}) {
  return (
    <MenuItem
      dense
      role="menuitemcheckbox"
      aria-checked={state.indeterminate ? 'mixed' : state.checked}
      disabled={disabled}
      sx={{ py: 0.25, minHeight: 0 }}
      onClick={onToggle}
    >
      {/* The row is the announced control; the box is only its visual. */}
      <Checkbox
        size="small"
        disableRipple
        edge="start"
        // A click on the hidden input would focus it; let the row take it.
        sx={{ py: 0.5, pointerEvents: 'none' }}
        checked={state.checked}
        indeterminate={state.indeterminate}
        tabIndex={-1}
        slotProps={{ input: { 'aria-hidden': true } }}
      />
      <ListItemText
        primary={option.label}
        slotProps={{
          primary: {
            variant: 'body2',
          },
        }}
      />
      {option.count !== undefined ? (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          {option.count}
        </Typography>
      ) : null}
    </MenuItem>
  )
}
